<template>
  <div class="text-center">
    <v-dialog v-model="dialog" width="560" persistent>
      <v-card>
        <v-card-title class="text-h5 blue darken-3 white--text">
          Cambiar estado de la inscripción
        </v-card-title>

        <v-card-text>
          <v-divider class="my-2"></v-divider>

          <!-- Información de la inscripción actual -->
          <v-alert v-if="inscripcionActual" type="info" dense class="mb-4">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-account</v-icon>
              <span><strong>Estudiante:</strong> {{ nombre_completo() }}</span>
            </div>
            <div class="d-flex align-center mt-1">
              <v-icon small class="mr-2">mdi-school</v-icon>
              <span><strong>Estado actual:</strong> {{ obtenerNombreEstado(inscripcionActual.estado_administrativo_id) }}</span>
            </div>
          </v-alert>

          <v-radio-group v-model="xestado" class="text-left mt-4">
            <v-radio
              v-for="n in xestados"
              :key="n.id"
              :label="n.text"
              :value="n"
              class="my-1"
            >
              <template v-slot:label>
                <span :class="{ 'font-weight-bold primary--text': n.id === xestado?.id }">
                  {{ n.text }}
                </span>
              </template>
            </v-radio>
          </v-radio-group>

          <!-- Sede + Programa cuando aplica -->
          <template v-if="requierePrograma">
            <v-divider class="mb-3" />
            <div class="caption font-weight-bold mb-2" :class="xestado.id === 4 ? 'blue--text text--darken-2' : 'orange--text text--darken-3'">
              <v-icon small class="mr-1" :color="xestado.id === 4 ? 'blue darken-2' : 'orange darken-3'">mdi-swap-horizontal</v-icon>
              {{ xestado.id === 4 ? 'Programa destino (al que se transfiere)' : 'Programa al que se cambió' }}
            </div>

            <!-- Sede -->
            <v-select
              v-model="selectedSede"
              :items="sedes"
              item-text="nombre"
              item-value="id"
              label="Sede *"
              outlined dense
              prepend-inner-icon="mdi-map-marker-outline"
              :loading="loadingSedes"
              @change="onChangeSede"
              class="mb-2"
            />

            <!-- Programa -->
            <v-autocomplete
              v-model="selectedPrograma"
              :items="programasOpciones"
              item-text="text"
              item-value="value"
              label="Programa *"
              outlined dense
              prepend-inner-icon="mdi-school"
              :loading="loadingProgramas"
              :disabled="!selectedSede || loadingProgramas"
              no-data-text="Sin programas para esta sede"
              clearable
            />
          </template>

          <p v-if="msgError" class="text-center red--text mt-2 mb-0">
            <v-icon small color="red">mdi-alert</v-icon> {{ msgError }}
          </p>
          <p v-if="msgSuccess" class="text-center green--text mt-2 mb-0">
            <v-icon small color="green">mdi-check-circle</v-icon> {{ msgSuccess }}
          </p>

          <v-divider class="my-3"></v-divider>

          <v-progress-linear v-if="loading" indeterminate color="indigo"></v-progress-linear>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cerrarDialog()" :disabled="loading">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!puedeGuardar || loading"
            @click="enviar()"
            :loading="loading"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import xaxios from '@/xaxios';
import gql from 'graphql-tag';

export default {
  name: 'DialogCambioEstado',
  data() {
    return {
      dialog: false,
      loading: false,
      idins: null,
      inscripcionActual: null,
      xestado: null,
      xestados: [
        { id: 1, text: 'Prospecto' },
        { id: 2, text: 'Preinscrito' },
        { id: 3, text: 'Inscrito' },
        { id: 4, text: 'Inscrito Transferido' },
        { id: 5, text: 'Retirado' },
        { id: 6, text: 'Retirado Cambiado' },
        { id: 7, text: 'Observado' },
        { id: 8, text: 'Reincorporado' },
      ],
      msgError: '',
      msgSuccess: '',

      sedes: [],
      loadingSedes: false,
      selectedSede: null,
      programasOpciones: [],
      loadingProgramas: false,
      selectedPrograma: null,
    };
  },

  computed: {
    requierePrograma() {
      return this.xestado?.id === 4 || this.xestado?.id === 6;
    },
    puedeGuardar() {
      if (!this.inscripcionActual || !this.xestado) return false;
      if (this.xestado.id === this.inscripcionActual.estado_administrativo_id) return false;
      if (this.requierePrograma && !this.selectedPrograma) return false;
      return true;
    },
    estadoActualNombre() {
      if (!this.inscripcionActual) return '';
      return this.obtenerNombreEstado(this.inscripcionActual.estado_ins);
    },
  },

  watch: {
    xestado(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.msgError = '';
        this.msgSuccess = '';
        this.selectedSede = null;
        this.selectedPrograma = null;
        this.programasOpciones = [];
        if (newVal?.id === 4 || newVal?.id === 6) {
          this.cargarSedes();
        }
      }
    },
  },

  methods: {
    openmodal(inscripcion) {
      this.resetearDialog();
      this.idins = inscripcion.id;
      this.inscripcionActual = { ...inscripcion };
      const estadoActual = this.xestados.find(e => e.id === inscripcion.estado_administrativo_id);
      this.xestado = estadoActual || null;
      this.dialog = true;
    },

    nombre_completo() {
      if (!this.inscripcionActual || !this.inscripcionActual.estudiante) return '';
      const est = this.inscripcionActual.estudiante;
      return `${est.nombres} ${est.pri_apellido} ${est.seg_apellido || ''}`.replace(/\s+/g, ' ').trim();
    },

    obtenerNombreEstado(idEstado) {
      const estado = this.xestados.find(e => e.id === idEstado);
      return estado ? estado.text : 'Desconocido';
    },

    async cargarSedes() {
      if (this.sedes.length > 0) return;
      this.loadingSedes = true;
      try {
        const unidad = this.$store.state.CARGO.sede.unidad_negocio;
        const { data } = await this.$apollo.query({
          query: gql`query { getSedes { id nombre unidad_negocio } }`,
          fetchPolicy: 'network-only',
        });
        this.sedes = (data.getSedes || []).filter(s => s.unidad_negocio === unidad);
      } catch (e) {
        window.console.warn('Error cargando sedes', e);
      } finally {
        this.loadingSedes = false;
      }
    },

    async onChangeSede(sedeId) {
      this.selectedPrograma = null;
      this.programasOpciones = [];
      if (!sedeId) return;
      this.loadingProgramas = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`query { getProgramas(cursor:1000000, estado:true, idsede:${sedeId}) { id codigo nombre_compuesto } }`,
          fetchPolicy: 'network-only',
        });
        this.programasOpciones = (data.getProgramas || []).map(p => ({
          text: `#${p.id} | ${p.codigo} | ${p.nombre_compuesto}`,
          value: p.id,
        }));
      } catch (e) {
        window.console.warn('Error cargando programas', e);
      } finally {
        this.loadingProgramas = false;
      }
    },

    async enviar() {
      if (!this.puedeGuardar) {
        this.msgError = 'Debes seleccionar un estado diferente al actual';
        return;
      }

      this.msgError = '';
      this.msgSuccess = '';
      this.loading = true;

      try {
        const url = `${process.env.VUE_APP_API_REST}/api/v1/inscripcion/${this.idins}/estado_administrativo`;
        const body = { estado_ins: this.xestado.id };
        if (this.requierePrograma && this.selectedPrograma) {
          body.programa_id = this.selectedPrograma;
          body.sede_id = this.selectedSede;
        }

        const response = await xaxios.patch(url, body);

        this.msgSuccess = response.data.data || 'Estado actualizado correctamente';

        this.$emit('estado-cambiado', {
          success: true,
          message: this.msgSuccess,
          inscripcionId: this.idins,
          estadoAnterior: this.inscripcionActual.estado_administrativo_id,
          estadoNuevo: this.xestado.id,
          nombreEstadoNuevo: this.xestado.text,
        });

        setTimeout(() => { this.cerrarDialog(); }, 500);

      } catch (error) {
        window.console.error('Error al cambiar estado:', error);
        let mensajeError = 'Error al cambiar el estado';
        if (error.response) {
          mensajeError = error.response.data?.message || error.response.statusText;
        } else if (error.request) {
          mensajeError = 'Error de conexión con el servidor';
        }
        this.msgError = mensajeError;
        this.$emit('estado-cambiado', {
          success: false,
          message: mensajeError,
          inscripcionId: this.idins,
          error,
        });
      } finally {
        this.loading = false;
      }
    },

    cerrarDialog() {
      this.dialog = false;
      setTimeout(() => { this.resetearDialog(); }, 300);
    },

    resetearDialog() {
      this.loading = false;
      this.idins = null;
      this.inscripcionActual = null;
      this.xestado = null;
      this.msgError = '';
      this.msgSuccess = '';
      this.selectedSede = null;
      this.selectedPrograma = null;
      this.programasOpciones = [];
    },
  },
};
</script>

<style scoped>
.v-alert {
  border-left: 4px solid #2196F3;
}
.v-radio {
  border-radius: 4px;
  padding: 4px 8px;
  transition: background-color 0.3s;
}
.v-radio:hover {
  background-color: #f5f5f5;
}
</style>
